import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import Header from "@/components/site/Header";
import AnnouncementBar from "@/components/site/AnnouncementBar";
import Footer from "@/components/site/Footer";
import StickyMobileCTA from "@/components/site/StickyMobileCTA";

const SORO_SCRIPT_SRC =
  "https://app.trysoro.com/api/embed/a546e61f-1593-4ce0-8858-a7a279377d6a";

export default function Blog() {
  useEffect(() => {
    // Inject the Soro widget script once per session.
    if (document.querySelector('script[data-soro="blog"]')) return;
    const s = document.createElement("script");
    s.src = SORO_SCRIPT_SRC;
    s.defer = true;
    s.dataset.soro = "blog";
    document.body.appendChild(s);
  }, []);

  return (
    <div data-testid="blog-page" className="bg-white text-slate-900">
      <AnnouncementBar />
      <Header />

      {/* Page hero */}
      <section
        data-testid="blog-hero"
        className="relative overflow-hidden bg-[#0A192F] text-white pt-36 pb-16 md:pt-44 md:pb-20"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 h-[420px] w-[420px] rounded-full bg-[#F97316]/15 blur-[120px]" />
          <div className="absolute -bottom-24 left-0 h-[320px] w-[320px] rounded-full bg-[#F97316]/10 blur-[120px]" />
        </div>

        <div className="rs-container relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-[#F97316]/15 border border-[#F97316]/30 px-4 py-1.5">
              <BookOpen className="h-3.5 w-3.5 text-[#F97316]" />
              <span className="text-xs uppercase tracking-[0.22em] font-bold text-[#F97316]">
                RS Handyman Journal
              </span>
            </div>

            <h1 className="mt-6 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight uppercase">
              The{" "}
              <span className="text-[#F97316]">Blog</span>.
            </h1>

            <p className="mt-5 text-lg md:text-xl text-slate-200/90 max-w-2xl leading-relaxed">
              Tips, project walkthroughs, and honest advice from a working handyman &amp;
              detailer serving Salisbury, Maryland and the Delmarva region.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="/#contact" data-testid="blog-estimate-cta" className="rs-btn-primary">
                Get a Free Estimate
                <ArrowRight className="h-4 w-4" />
              </a>
              <a href="/#pricing" data-testid="blog-prices-cta" className="rs-btn-ghost">
                View Our Prices
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Soro widget */}
      <section className="rs-section bg-white">
        <div className="rs-container">
          <div
            data-testid="blog-widget-wrapper"
            className="rounded-3xl border border-slate-200 bg-[#F8FAFC] p-5 sm:p-8 md:p-10 min-h-[520px]"
          >
            {/* Soro Blog widget embed target */}
            <div id="soro-blog" data-testid="soro-blog-mount" />

            {/* Fallback shown only while the Soro script is loading */}
            <noscript>
              <p className="text-slate-600">
                Enable JavaScript to view the RS Handyman blog.
              </p>
            </noscript>
          </div>

          <p className="mt-6 text-center text-sm text-slate-500">
            New posts added regularly. Have a project question?{" "}
            <a href="/#contact" className="text-[#F97316] font-semibold hover:underline">
              Ask us directly
            </a>
            .
          </p>
        </div>
      </section>

      <Footer />
      <StickyMobileCTA />
    </div>
  );
}
